import { useRef, useState, useCallback} from 'react';
import { UploadStatusModal } from '@/components/ui';
import { useReceiptUpload } from '../hooks/useReceiptUpload';
import { ReceiptPreviewCard } from './ReceiptPreviewCard';
import { RECEIPT_ACCEPTED_TYPES } from '../types';

interface ReceiptUploaderProps {
  onComplete?: () => void;
  projects?: Array<{ id: string; name: string }>;
  defaultProjectId?: string | null;
  defaultSupplierId?: string | null;
}

export function ReceiptUploader({
  onComplete,
  projects = [],
  defaultProjectId,
  defaultSupplierId,
}: ReceiptUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Upload status modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    status: 'uploading' | 'success' | 'error';
    totalCount: number;
    successCount: number;
    errorCount: number;
  }>({
    isOpen: false,
    status: 'uploading',
    totalCount: 0,
    successCount: 0,
    errorCount: 0,
  });

  const handleUploadStart = useCallback((count: number) => {
    setModalState({
      isOpen: true,
      status: 'uploading',
      totalCount: count,
      successCount: 0,
      errorCount: 0,
    });
  }, []);

  const handleUploadComplete = useCallback((successCount: number, errorCount: number, totalCount: number) => {
    setModalState({
      isOpen: true,
      status: errorCount > 0 ? 'error' : 'success',
      totalCount,
      successCount,
      errorCount,
    });
  }, []);

  const {
    receipts,
    isProcessing,
    addFiles,
    removeReceipt,
    setSelectedProject,
    setSelectedSupplier,
    uploadReceipts,
    clearAll,
  } = useReceiptUpload({
    defaultProjectId,
    defaultSupplierId,
    onUploadStart: handleUploadStart,
    onUploadComplete: handleUploadComplete,
  });

  const handleModalClose = useCallback(() => {
  setModalState((prev) => ({ ...prev, isOpen: false }));
  // If uploads were successful, call onComplete and clear the list
  if (modalState.status === 'success') {
    onComplete?.();
    clearAll();
  }
}, [modalState.status, onComplete, clearAll]);

  
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(e.target.files);
      }
      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files).filter(
        (file) =>
          file.type.startsWith('image/') || file.type === 'application/pdf'
      );

      if (files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles]
  );

  const readyCount = receipts.filter((r) => r.status === 'ready').length;
  const uploadedCount = receipts.filter((r) => r.status === 'uploaded').length;

  // Hide project/supplier select when defaults are provided
  const showProjectSelect = !defaultProjectId;
  const showSupplierSelect = !defaultSupplierId;

  const acceptTypes = RECEIPT_ACCEPTED_TYPES.join(',');

  return (
    <>
      <div className="space-y-6">
        {/* Drop Zone */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
            ${
              isDragging
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 hover:border-primary-400 hover:bg-secondary-50'
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />

          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-secondary-900 font-medium">
                Drop receipts here or click to browse
              </p>
              <p className="text-secondary-500 text-sm mt-1">
                JPEG, PNG, HEIC, PDF up to 25MB
              </p>
            </div>
          </div>
        </div>

        {/* Receipt Grid */}
        {receipts.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-600">
                {receipts.length} receipt{receipts.length !== 1 ? 's' : ''} selected
                {uploadedCount > 0 && ` • ${uploadedCount} uploaded`}
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-secondary-500 hover:text-secondary-700"
                disabled={isProcessing}
              >
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-visible">
              {receipts.map((receipt) => (
                <ReceiptPreviewCard
                  key={receipt.id}
                  receipt={receipt}
                  onRemove={removeReceipt}
                  onProjectChange={setSelectedProject}
                  onSupplierChange={setSelectedSupplier}
                  projects={projects}
                  showProjectSelect={showProjectSelect}
                  showSupplierSelect={showSupplierSelect}
                />
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={uploadReceipts}
                disabled={readyCount === 0 || isProcessing}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-colors
                  ${
                    readyCount === 0 || isProcessing
                      ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }
                `}
              >
                Upload {readyCount} Receipt{readyCount !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Upload Status Modal */}
      <UploadStatusModal
        isOpen={modalState.isOpen}
        status={modalState.status}
        totalCount={modalState.totalCount}
        successCount={modalState.successCount}
        errorCount={modalState.errorCount}
        onClose={handleModalClose}
        itemType="receipt"
      />
    </>
  );
}